/**
 * GRANULLO PHASE 2 — CBDC Simulation Context
 * Provides state management for the Interactive CBDC Simulation
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

// CBDC Node Types
const CBDC_NODE_TYPES = {
  CENTRAL_BANK: 'central_bank',
  COMMERCIAL_BANK: 'commercial_bank',
  MERCHANT: 'merchant',
  CONSUMER: 'consumer',
  INFRASTRUCTURE: 'infrastructure'
};

// CBDC Phases
const CBDC_PHASES = {
  IDLE: 'idle',
  ISSUANCE: 'issuance',
  DISTRIBUTION: 'distribution',
  MERCHANT_FLOW: 'merchant_flow',
  CONSUMER_FLOW: 'consumer_flow',
  PROPAGATION: 'propagation'
};

// Default CBDC Network Structure
const DEFAULT_CBDC_NETWORK = [
  // Central Bank (Source)
  { 
    id: 'central-bank', 
    type: CBDC_NODE_TYPES.CENTRAL_BANK, 
    name: 'Central Bank', 
    balance: 0, 
    throughput: 100,
    status: 'idle',
    position: { x: 50, y: 10 }
  },
  
  // Commercial Banks (Distribution Layer)
  { 
    id: 'bank-a', 
    type: CBDC_NODE_TYPES.COMMERCIAL_BANK, 
    name: 'Bank A', 
    balance: 0, 
    throughput: 80,
    status: 'idle',
    position: { x: 25, y: 30 }
  },
  { 
    id: 'bank-b', 
    type: CBDC_NODE_TYPES.COMMERCIAL_BANK, 
    name: 'Bank B', 
    balance: 0, 
    throughput: 75,
    status: 'idle',
    position: { x: 50, y: 30 }
  },
  { 
    id: 'bank-c', 
    type: CBDC_NODE_TYPES.COMMERCIAL_BANK, 
    name: 'Bank C', 
    balance: 0, 
    throughput: 70,
    status: 'idle',
    position: { x: 75, y: 30 }
  },
  
  // Merchants (Settlement Layer)
  { 
    id: 'merchant-1', 
    type: CBDC_NODE_TYPES.MERCHANT, 
    name: 'Retail Chain', 
    balance: 0, 
    throughput: 50,
    status: 'idle',
    position: { x: 15, y: 55 }
  },
  { 
    id: 'merchant-2', 
    type: CBDC_NODE_TYPES.MERCHANT, 
    name: 'Energy Provider', 
    balance: 0, 
    throughput: 60,
    status: 'idle',
    position: { x: 40, y: 55 }
  },
  { 
    id: 'merchant-3', 
    type: CBDC_NODE_TYPES.MERCHANT, 
    name: 'Healthcare', 
    balance: 0, 
    throughput: 45,
    status: 'idle',
    position: { x: 60, y: 55 }
  },
  { 
    id: 'merchant-4', 
    type: CBDC_NODE_TYPES.MERCHANT, 
    name: 'Infrastructure', 
    balance: 0, 
    throughput: 55,
    status: 'idle',
    position: { x: 85, y: 55 }
  },
  
  // Consumers (Transaction Layer)
  { 
    id: 'consumer-pool', 
    type: CBDC_NODE_TYPES.CONSUMER, 
    name: 'Consumer Network', 
    balance: 0, 
    throughput: 40,
    status: 'idle',
    position: { x: 50, y: 80 }
  }
];

// Default Flow Connections
const DEFAULT_CBDC_FLOWS = [
  // Central Bank → Commercial Banks
  { id: 'flow-cb-a', from: 'central-bank', to: 'bank-a', amount: 0, status: 'idle' },
  { id: 'flow-cb-b', from: 'central-bank', to: 'bank-b', amount: 0, status: 'idle' },
  { id: 'flow-cb-c', from: 'central-bank', to: 'bank-c', amount: 0, status: 'idle' },
  
  // Commercial Banks → Merchants
  { id: 'flow-a-m1', from: 'bank-a', to: 'merchant-1', amount: 0, status: 'idle' },
  { id: 'flow-a-m2', from: 'bank-a', to: 'merchant-2', amount: 0, status: 'idle' },
  { id: 'flow-b-m2', from: 'bank-b', to: 'merchant-2', amount: 0, status: 'idle' },
  { id: 'flow-b-m3', from: 'bank-b', to: 'merchant-3', amount: 0, status: 'idle' },
  { id: 'flow-c-m3', from: 'bank-c', to: 'merchant-3', amount: 0, status: 'idle' },
  { id: 'flow-c-m4', from: 'bank-c', to: 'merchant-4', amount: 0, status: 'idle' },
  
  // Merchants → Consumers
  { id: 'flow-m1-c', from: 'merchant-1', to: 'consumer-pool', amount: 0, status: 'idle' },
  { id: 'flow-m2-c', from: 'merchant-2', to: 'consumer-pool', amount: 0, status: 'idle' },
  { id: 'flow-m3-c', from: 'merchant-3', to: 'consumer-pool', amount: 0, status: 'idle' },
  { id: 'flow-m4-c', from: 'merchant-4', to: 'consumer-pool', amount: 0, status: 'idle' }
];

const CBDCSimulationContext = createContext(null);

export function CBDCSimulationProvider({ children }) {
  // Simulation State
  const [nodes, setNodes] = useState(DEFAULT_CBDC_NETWORK);
  const [flows, setFlows] = useState(DEFAULT_CBDC_FLOWS);
  const [phase, setPhase] = useState(CBDC_PHASES.IDLE);
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulationSpeed, setSimulationSpeed] = useState(1);
  
  // Control Parameters
  const [issuanceLevel, setIssuanceLevel] = useState(1000); // Million units
  const [transactionDelay, setTransactionDelay] = useState(0); // ms
  const [networkCongestion, setNetworkCongestion] = useState(0); // 0-100%
  
  // System Metrics
  const [metrics, setMetrics] = useState({
    totalIssuance: 0,
    circulationVelocity: 0,
    settlementLatency: 0,
    networkCoverage: 0,
    cascadeRisk: 0,
    exposureDelta: 0
  });
  
  // Animation frame ref
  const animationRef = useRef(null);
  const phaseTimerRef = useRef(null);
  
  // Reset simulation
  const resetSimulation = useCallback(() => {
    setNodes(DEFAULT_CBDC_NETWORK);
    setFlows(DEFAULT_CBDC_FLOWS);
    setPhase(CBDC_PHASES.IDLE);
    setIsSimulating(false);
    setMetrics({
      totalIssuance: 0,
      circulationVelocity: 0,
      settlementLatency: 0,
      networkCoverage: 0,
      cascadeRisk: 0,
      exposureDelta: 0
    });
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    if (phaseTimerRef.current) clearTimeout(phaseTimerRef.current);
  }, []);
  
  // Calculate cascade risk based on network state
  const calculateCascadeRisk = useCallback((currentNodes, currentFlows) => {
    const activeNodes = currentNodes.filter(n => n.status !== 'idle').length;
    const congestionFactor = networkCongestion / 100;
    const delayFactor = transactionDelay / 1000;
    
    return Math.min(100, Math.round(
      (activeNodes / currentNodes.length) * 30 +
      congestionFactor * 40 +
      delayFactor * 30
    ));
  }, [networkCongestion, transactionDelay]);
  
  // Update node status
  const updateNodeStatus = useCallback((nodeId, status, balance) => {
    setNodes(prev => prev.map(node => 
      node.id === nodeId 
        ? { ...node, status, balance: balance ?? node.balance }
        : node
    ));
  }, []);
  
  // Update flow status
  const updateFlowStatus = useCallback((flowId, status, amount) => {
    setFlows(prev => prev.map(flow =>
      flow.id === flowId
        ? { ...flow, status, amount: amount ?? flow.amount }
        : flow
    ));
  }, []);
  
  // Run simulation
  const runSimulation = useCallback(() => {
    if (isSimulating) return;
    
    setIsSimulating(true);
    const baseDelay = 1500 / simulationSpeed;
    const congestionDelay = (networkCongestion / 100) * 500;
    const totalDelay = baseDelay + congestionDelay + transactionDelay;
    
    // Phase 1: Issuance
    setPhase(CBDC_PHASES.ISSUANCE);
    updateNodeStatus('central-bank', 'distributing', issuanceLevel);
    setMetrics(prev => ({ ...prev, totalIssuance: issuanceLevel }));
    
    // Phase 2: Distribution to Commercial Banks
    phaseTimerRef.current = setTimeout(() => {
      setPhase(CBDC_PHASES.DISTRIBUTION);
      
      // Activate flows from Central Bank
      ['flow-cb-a', 'flow-cb-b', 'flow-cb-c'].forEach((flowId, i) => {
        setTimeout(() => {
          updateFlowStatus(flowId, 'in_transit', Math.round(issuanceLevel / 3));
        }, i * 200);
      });
      
      // Update bank nodes
      setTimeout(() => {
        updateNodeStatus('bank-a', 'receiving', Math.round(issuanceLevel / 3));
        updateNodeStatus('bank-b', 'receiving', Math.round(issuanceLevel / 3));
        updateNodeStatus('bank-c', 'receiving', Math.round(issuanceLevel / 3));
        
        ['flow-cb-a', 'flow-cb-b', 'flow-cb-c'].forEach(flowId => {
          updateFlowStatus(flowId, 'settled');
        });
        
        setMetrics(prev => ({
          ...prev,
          circulationVelocity: 1.2,
          settlementLatency: Math.round(totalDelay),
          networkCoverage: 33
        }));
      }, totalDelay * 0.5);
      
    }, totalDelay);
    
    // Phase 3: Merchant Settlement
    phaseTimerRef.current = setTimeout(() => {
      setPhase(CBDC_PHASES.MERCHANT_FLOW);
      
      // Bank → Merchant flows
      const merchantFlows = ['flow-a-m1', 'flow-a-m2', 'flow-b-m2', 'flow-b-m3', 'flow-c-m3', 'flow-c-m4'];
      merchantFlows.forEach((flowId, i) => {
        setTimeout(() => {
          updateFlowStatus(flowId, 'in_transit', Math.round(issuanceLevel / 6));
        }, i * 150);
      });
      
      setTimeout(() => {
        updateNodeStatus('bank-a', 'distributing');
        updateNodeStatus('bank-b', 'distributing');
        updateNodeStatus('bank-c', 'distributing');
        updateNodeStatus('merchant-1', 'receiving', Math.round(issuanceLevel / 6));
        updateNodeStatus('merchant-2', 'receiving', Math.round(issuanceLevel / 3));
        updateNodeStatus('merchant-3', 'receiving', Math.round(issuanceLevel / 3));
        updateNodeStatus('merchant-4', 'receiving', Math.round(issuanceLevel / 6));
        
        merchantFlows.forEach(flowId => updateFlowStatus(flowId, 'settled'));
        
        setMetrics(prev => ({
          ...prev,
          circulationVelocity: 2.1,
          networkCoverage: 67,
          cascadeRisk: calculateCascadeRisk(nodes, flows)
        }));
      }, totalDelay * 0.6);
      
    }, totalDelay * 2);
    
    // Phase 4: Consumer Transactions
    phaseTimerRef.current = setTimeout(() => {
      setPhase(CBDC_PHASES.CONSUMER_FLOW);
      
      const consumerFlows = ['flow-m1-c', 'flow-m2-c', 'flow-m3-c', 'flow-m4-c'];
      consumerFlows.forEach((flowId, i) => {
        setTimeout(() => {
          updateFlowStatus(flowId, 'in_transit', Math.round(issuanceLevel / 4));
        }, i * 100);
      });
      
      setTimeout(() => {
        updateNodeStatus('merchant-1', 'distributing');
        updateNodeStatus('merchant-2', 'distributing');
        updateNodeStatus('merchant-3', 'distributing');
        updateNodeStatus('merchant-4', 'distributing');
        updateNodeStatus('consumer-pool', 'receiving', issuanceLevel);
        
        consumerFlows.forEach(flowId => updateFlowStatus(flowId, 'settled'));
        
        setMetrics(prev => ({
          ...prev,
          circulationVelocity: 3.4,
          networkCoverage: 89
        }));
      }, totalDelay * 0.5);
      
    }, totalDelay * 3);
    
    // Phase 5: Systemic Propagation
    phaseTimerRef.current = setTimeout(() => {
      setPhase(CBDC_PHASES.PROPAGATION);
      
      // Calculate final exposure delta
      const exposureDelta = Math.round(
        (issuanceLevel * 0.012) + // Base exposure from issuance
        (networkCongestion * 0.05) + // Congestion impact
        (transactionDelay * 0.002) // Delay impact
      );
      
      setMetrics(prev => ({
        ...prev,
        circulationVelocity: 4.2,
        networkCoverage: 100,
        cascadeRisk: calculateCascadeRisk(nodes, flows),
        exposureDelta
      }));
      
      // Complete simulation
      setTimeout(() => {
        setIsSimulating(false);
      }, totalDelay * 0.5);
      
    }, totalDelay * 4);
    
  }, [isSimulating, simulationSpeed, issuanceLevel, transactionDelay, networkCongestion, calculateCascadeRisk, updateNodeStatus, updateFlowStatus, nodes, flows]);
  
  // Cleanup on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (phaseTimerRef.current) clearTimeout(phaseTimerRef.current);
    };
  }, []);
  
  const value = {
    // State
    nodes,
    flows,
    phase,
    isSimulating,
    simulationSpeed,
    metrics,
    
    // Controls
    issuanceLevel,
    setIssuanceLevel,
    transactionDelay,
    setTransactionDelay,
    networkCongestion,
    setNetworkCongestion,
    setSimulationSpeed,
    
    // Actions
    runSimulation,
    resetSimulation,
    
    // Constants
    CBDC_NODE_TYPES,
    CBDC_PHASES
  };
  
  return (
    <CBDCSimulationContext.Provider value={value}>
      {children}
    </CBDCSimulationContext.Provider>
  );
}

export function useCBDCSimulation() {
  const context = useContext(CBDCSimulationContext);
  if (!context) {
    throw new Error('useCBDCSimulation must be used within CBDCSimulationProvider');
  }
  return context;
}

export { CBDC_NODE_TYPES, CBDC_PHASES };
export default CBDCSimulationContext;
